import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { f7 } from 'framework7-react';
import { ApiError, errorMessage } from './errors';
import { getErrorT } from '@/i18n';

function handleError(error: unknown) {
  if (!(error instanceof ApiError)) return;
  // system.validationFailed is handled inline by individual mutations.
  if (error.code === 'system.validationFailed') return;
  // A 401 is an expected auth-lifecycle event, not an error to surface: the
  // token interceptor already called logout(), which flips isAuthenticated
  // and makes App.tsx route to /login. Popping a modal here (once per
  // in-flight query/mutation, so often several at once) on every token
  // expiry is just noise on top of an automatic redirect.
  //
  // Match on the STATUS as well as the code. Not every 401 carries our error
  // contract — a rejection from the JWT guard arrives as a plain
  // `{ message: "401" }`, which `ApiError.from` degrades to the
  // `system.internal` fallback code. Matching the code alone would let those
  // through as a bogus "something went wrong" alert on an ordinary logout.
  if (error.code === 'system.unauthenticated' || error.status === 401) return;
  const msg = errorMessage({ code: error.code, params: error.params }, getErrorT());
  f7.dialog.alert(msg, 'Error');
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.meta?.handlesOwnError === true) return;
      handleError(error);
    },
  }),
  queryCache: new QueryCache({
    onError: handleError,
  }),
});
